// districtFilter.js — the district filter's SEARCH semantics on the client, from the four arrays the
// tree commits (districtCodes, districtOwnCodes, districtCodesNot, districtOwnCodesNot). Pure; tested
// in districtFilter.test.mjs. Plain Node must be able to import it (no React, no db).
//
// The server's definition is the `dset` CTE of search_agent_listing_clusters (core 2477; rule 0886,
// EXPLICIT_DESCENDANT_INCLUDE_OVERRIDES_ANCESTOR_EXCLUDE). Everything here mirrors it:
//   allowed   = includes ∪ their descendants ∪ own includes — null when there is no include, or when
//               EVERY include is a carve-out (an include strictly inside an excluded subtree; an own
//               include equal to or under an excluded code)
//   excluded  = expand(districtCodesNot) ∪ districtOwnCodesNot, minus each carve-out's subtree and
//               every own include — it vetoes first, on the pin OR the declared district
//   parentish = ancestors of the includes → 'unknown' (coded too coarse to confirm)

export const DISTRICT_KEYS = ['districtCodes', 'districtOwnCodes', 'districtCodesNot', 'districtOwnCodesNot']

// ⚠️ A regional town IS its municipality in a search (owner 2026-10-30: "when someone looks for gori show all gori"). Gori
// town held 27 land leads while the villages filed under MUN_GORI held 23 more, and both chips read "Gori"; the box picks
// the town (searchCompiler v16: TWN_ beats MUN_). Applied where a search LEAVES the browser (searchWire, getClientLeads)
// and where rows are re-judged (districtFilterSets), never to the committed state, so the chip and the URL keep what was
// picked. Same table as the public site's (server/lib/core_public_site.js TOWN_IS_MUNICIPALITY) — change both together.
// Borjomi is NOT here: its municipality holds Bakuriani, a separate place.
export const TOWN_IS_MUNICIPALITY = { TWN_GORI: 'MUN_GORI', TWN_KOBULETI: 'MUN_KOBULETI', TWN_MTSKHETA: 'MUN_MTSKHETA',
  TWN_TELAVI: 'MUN_TELAVI', TWN_ZUGDIDI: 'MUN_ZUGDIDI' }
/** The district codes a search asks for: a TOWN_IS_MUNICIPALITY town as its municipality, de-duplicated. */
export const searchedDistricts = codes => [...new Set((codes || []).map(c => TOWN_IS_MUNICIPALITY[c] || c))]
const NOT_OF = { districtCodes: 'districtCodesNot', districtOwnCodes: 'districtOwnCodesNot' }

/** The parent walk over a districts list ({code, parent_code}). A cycle or self-parent degrades to "no further ancestors". */
export function districtTreeIndex(districts) {
  const parentOf = new Map((districts || []).map(d => [d.code, d.parent_code]))
  const ancestorsOf = code => {
    const out = []
    const seen = new Set([code])
    let p = parentOf.get(code)
    while (p && !seen.has(p)) { out.push(p); seen.add(p); p = parentOf.get(p) }
    return out
  }
  return { parentOf, ancestorsOf, codes: [...parentOf.keys()] }
}

const list = (f, k) => (f && Array.isArray(f[k]) ? f[k] : [])

/** True when any of the four district arrays is non-empty. */
export function districtFilterActive(f) {
  return DISTRICT_KEYS.some(k => list(f, k).length > 0)
}

/** Every code in the tree that has one of `roots` as a strict ancestor. */
function descendantsOfAny(tree, roots) {
  const r = new Set(roots)
  if (!r.size) return []
  return tree.codes.filter(c => tree.ancestorsOf(c).some(a => r.has(a)))
}

/** { allowed, excluded, parentish } as Sets (allowed/excluded null = no restriction), the client copy of 2477's dset. */
export function districtFilterSets(f, tree) {
  const inc = searchedDistricts(list(f, 'districtCodes')), own = list(f, 'districtOwnCodes')
  const not = new Set(list(f, 'districtCodesNot')), ownNot = list(f, 'districtOwnCodesNot')
  const underNot = c => tree.ancestorsOf(c).some(a => not.has(a))   // strictly inside an excluded subtree
  const carveInc = inc.filter(underNot)
  const anyInclude = inc.length > 0 || own.length > 0
  const restricting = inc.some(c => !underNot(c)) || own.some(o => !(not.has(o) || underNot(o)))
  const allowed = anyInclude && restricting
    ? new Set([...inc, ...descendantsOfAny(tree, inc), ...own]) : null
  let excluded = null
  if (not.size || ownNot.length) {
    const back = new Set([...carveInc, ...descendantsOfAny(tree, carveInc), ...own])
    excluded = new Set([...not, ...descendantsOfAny(tree, [...not]), ...ownNot].filter(c => !back.has(c)))
  }
  // ⚠️ Client rule kept (pre-0886): parentish comes from the subtree includes only, so a row coded at a
  // raion is a mismatch, not 'unknown', when only a child's own bucket is picked. 2477 also walks own codes.
  const parentish = new Set(inc.flatMap(c => tree.ancestorsOf(c)))
  return { allowed, excluded, parentish }
}

/**
 * The row verdict ('pass' | 'unknown' | 'mismatch') for a listing-shaped row
 * ({ district_code, declared_district_code, district_votes }), or null when no district array is set.
 * Mirrors 2477's district_verdict: the exclude veto first, then allowed, then the dispute vote (0101).
 */
export function districtVerdictFn(f, tree) {
  if (!districtFilterActive(f)) return null
  const { allowed, excluded, parentish } = districtFilterSets(f, tree)
  const check = code => {
    if (code == null) return 'unknown'
    if (allowed.has(code)) return 'pass'
    if (parentish.has(code)) return 'unknown'
    return 'mismatch'
  }
  return l => {
    const code = l.district_code, declared = l.declared_district_code
    if (excluded && ((code != null && excluded.has(code)) || (declared != null && excluded.has(declared)))) return 'mismatch'
    if (!allowed) return 'pass'
    if (!declared || declared === code) return check(code)
    const va = check(declared), vb = check(code)
    const vd = l.district_votes?.declared ?? 0, vp = l.district_votes?.polygon ?? 0
    if (vd === vp) {   // tie or 0 votes → keep-both: pass beats unknown beats mismatch
      if (va === 'pass' || vb === 'pass') return 'pass'
      return (va === 'unknown' || vb === 'unknown') ? 'unknown' : 'mismatch'
    }
    const prim = vd > vp ? va : vb, sec = vd > vp ? vb : va
    if (prim === 'pass') return 'pass'
    if (prim === 'unknown' || sec === 'pass' || sec === 'unknown') return 'unknown'
    return 'mismatch'
  }
}

/**
 * The city as a district restriction (2026-10-22). Returns `f` unchanged, or a copy whose districtCodes
 * also carry the city's codes. The city rides as includes only when nothing else confines the search:
 *   · all four arrays empty → every city code (unchanged since 2026-07-20);
 *   · an include that is not a carve-out exists → nothing added, the picks already confine it;
 *   · otherwise (only ✕, or every include a carve-out) → the city's codes OUTSIDE every excluded subtree.
 * Injecting a code inside an excluded subtree would make it a 0886 carve-out and give the subtree back:
 * "✕ Saburtalo" with all of Tbilisi injected removed only listings coded exactly TBS_SABURTALO.
 */
export function cityScopedDistrictFilters(f, cityCodes, tree) {
  if (!cityCodes || !cityCodes.length) return f
  if (!districtFilterActive(f)) return { ...f, districtCodes: [...cityCodes] }
  const inc = list(f, 'districtCodes'), own = list(f, 'districtOwnCodes')
  const not = new Set(list(f, 'districtCodesNot'))
  const inNotSubtree = c => not.has(c) || tree.ancestorsOf(c).some(a => not.has(a))
  const underNot = c => tree.ancestorsOf(c).some(a => not.has(a))
  if (inc.some(c => !underNot(c)) || own.some(o => !inNotSubtree(o))) return f
  const add = cityCodes.filter(c => !inNotSubtree(c))
  return { ...f, districtCodes: [...new Set([...inc, ...add])] }
}

/** A city change: drop every district code (all four arrays) that is not in the new city. Null when nothing changes. */
export function pruneDistrictsToCity(f, inCity) {
  let changed = false
  const patch = {}
  for (const k of DISTRICT_KEYS) {
    const cur = list(f, k)
    const kept = cur.filter(code => inCity(code))
    if (kept.length !== cur.length) changed = true
    patch[k] = kept
  }
  return changed ? patch : null
}

/**
 * A city change, the rest of the place (audit 2026-10-29 D2): street, metro station and nearby point. Kept only when
 * the catalog can say they lie in the new city; otherwise dropped. Null when nothing changes.
 * ⚠️ pruneDistrictsToCity alone let a Tbilisi street / station / 300 m point ride into Zugdidi — the city said
 * Zugdidi while the request still asked for a Tbilisi street, which can only answer zero.
 *   · streets — the client holds no street→city map, so a street never survives a change to another city;
 *   · metro   — kept only when `metroInCity` says the new city has one (only Tbilisi does);
 *   · near    — kept only when `nearInCity(near)` answers true (its gazetteer row's district lies in the new city).
 * The caller skips this for "all cities" and for the same city: neither moves the place.
 */
export function pruneLocationToCity(f, { metroInCity = false, nearInCity = () => false } = {}) {
  const patch = {}
  if (list(f, 'streetIds').length) { patch.streetIds = []; patch.streetNearbyM = 0 }
  if (list(f, 'metroIds').length && !metroInCity) patch.metroIds = []
  if (f.near && !nearInCity(f.near)) patch.near = null
  return Object.keys(patch).length ? patch : null
}

/** Add codes to an include array (districtCodes / districtOwnCodes) and take them out of its ✕ twin: one code, one state. */
export function addDistrictIncludes(f, key, codes) {
  const notKey = NOT_OF[key]
  const add = codes || []
  const out = { ...f, [key]: [...new Set([...list(f, key), ...add])] }
  if (notKey && list(f, notKey).some(c => add.includes(c))) out[notKey] = list(f, notKey).filter(c => !add.includes(c))
  return out
}

/** Add codes to a ✕ array (districtCodesNot / districtOwnCodesNot) and take them out of its ✓ twin (2026-10-22: a typed
 *  "არა ვაკე" after a typed "ვაკე" must not leave Vake in both arrays). */
export function addDistrictExcludes(f, notKey, codes) {
  const key = Object.keys(NOT_OF).find(k => NOT_OF[k] === notKey)
  const add = codes || []
  const out = { ...f, [notKey]: [...new Set([...list(f, notKey), ...add])] }
  if (key && list(f, key).some(c => add.includes(c))) out[key] = list(f, key).filter(c => !add.includes(c))
  return out
}
